import { HttpHeaders } from '@angular/common/http';
import { Component, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Params, Router, RouterOutlet, UrlSerializer } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import * as Highcharts from 'highcharts';
import { Moment } from 'moment';
import {
  Observable,
  Subject,
  combineLatest,
  distinctUntilChanged,
  filter,
  from,
  takeUntil,
} from 'rxjs';
import { Stonly } from '@inmoment/stonly';
import { TabObject } from '@inmoment/the-kitchen';

import { routerTransition } from './app-animate';
import { getTelemetryConfig } from './app-config';
import { AppService } from './app.service';
import { SnackbarData } from './components/xi-components/lex-xi-snack-bar/lex-xi-snack-bar.component';
import { SessionExpirationWarningComponent } from './modules/account/components/session-expiration-warning/session-expiration-warning.component';
import { ChartDataService } from './modules/widget/services/chart-data.service';
import { Account, SemantriaAccount } from './providers/bridge-api/account.service';
import {
  AuthService,
  ExpirationEvent,
  LogoutEvent,
  Session,
} from './providers/bridge-api/auth.service';
import { SemantriaPreferences } from './providers/bridge-api/preferences.service';
import { RealtimeService } from './providers/realtime.service';
import { ConfigurationService } from './providers/semantria-api/configuration.service';
import { Group, GroupService } from './providers/semantria-api/group.service';
import {
  AccountCounts,
  StorageAccountService,
} from './providers/storage-api/blender-account.service';
import { DEFAULT_PROJECT_COLOR_PALETTE } from './providers/storage-api/project.service';
import { AlertService } from './services/alert.service';
import { DialogService } from './services/dialog.service';
import { IconService } from './services/icon.service';
import { LocalStorageService } from './services/local-storage.service';
import { LoggerConfiguration, LoggerLevel, LoggerService } from './services/logger.service';
import { MobileMode, MobileModeService } from './services/mobile-mode.service';
import { NavbarService } from './services/navbar.service';
import { TimezoneService } from './services/timezone.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routerTransition],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private tabMapping: {
    id: string;
    route: string;
    title: string;
    icon: string;
    disabled: boolean;
  }[] = [];
  public tabMenu: TabObject[];

  public isLoading = { account: false };

  public appVersion = (window as any).appVersion;
  public account: Account;
  public userHomepage: string;
  public userTimezone: string;

  public visibleGroups: Group[];
  public selectedGroup: Group;
  public session: Session;
  public accountCounts: AccountCounts;
  public groupSelectorTooltip: string;
  public groupTab: boolean;
  public activeTab: string;
  public navbarDataLoaded: boolean;
  public sessionExpiredDialogRef: MatDialogRef<SessionExpirationWarningComponent> | undefined;

  private translationResources = {
    projects: '',
    configs: '',
    routes: '',
    metadata: '',
    plans: '',
    groups: '',
    users: '',
    groupselect: '',
  };
  private stonly?: Stonly;
  private user$: Observable<Session>;

  snackbarData: SnackbarData = {
    id: '',
    message: '',
    duration: 0,
    mode: 'default',
    autoDismiss: true,
  };
  public showTimezoneModal = false;
  public expiration: Moment;

  showDownloadCenter = false;
  showMobileModeSelector = false;

  isChangeLanguageModalOpen = false;
  userLangCode: string;
  unsub$ = new Subject<void>();

  @ViewChild('routerOutlet') routerOutlet!: RouterOutlet;

  public localStorageService = inject(LocalStorageService);

  constructor(
    chartDataService: ChartDataService,
    public authService: AuthService,
    public timezoneService: TimezoneService,
    private router: Router,
    private appService: AppService,
    private configService: ConfigurationService,
    private accountService: SemantriaAccount,
    private storageAccountService: StorageAccountService,
    private preferencesService: SemantriaPreferences,
    private alertService: AlertService,
    private iconService: IconService,
    private groupService: GroupService,
    private navbarService: NavbarService,
    private serializer: UrlSerializer,
    private dialogService: DialogService,
    protected logger: LoggerService,
    private translocoService: TranslocoService,
    private realtimeService: RealtimeService,
    private mobileModeService: MobileModeService
  ) {
    // eslint-disable-next-line
    chartDataService || true; // this initializes the service
    Highcharts.setOptions({
      colors: DEFAULT_PROJECT_COLOR_PALETTE,
      plotOptions: {
        column: {
          minPointLength: 3,
        },
      },
      lang: { thousandsSep: ',' },
    });

    this.iconService.registerSvgIcons();

    this.authService.loggedout.subscribe((data) => {
      this.reset(data);
    });

    this.authService.sessionWarning.subscribe((data: ExpirationEvent) => {
      this.openSessionWarning(data);
    });

    const navEndEvent$ = this.router.events.pipe(
      takeUntil(this.unsub$),
      filter((event) => event instanceof NavigationEnd),
      distinctUntilChanged(
        (a: NavigationEnd, b: NavigationEnd) =>
          this.stripMatrixParams(a.url) === this.stripMatrixParams(b.url)
      )
    );

    navEndEvent$.subscribe((event: NavigationEnd) => {
      this.setTabData(event.url);

      this.setState(this.routerOutlet);
    });
    const translations$ = this.translocoService.selectTranslateObject('app');
    combineLatest([navEndEvent$, translations$])
      .pipe(takeUntil(this.unsub$))
      .subscribe(([_, translations]) => {
        this.translationResources = {
          projects: translations.projects,
          configs: translations.configs,
          routes: translations.routes,
          metadata: translations.metadata,
          plans: translations.plans,
          groups: translations.groups,
          users: translations.users,
          groupselect: translations.groupselect,
        };

        if (this.canShow) {
          if (!this.account && !this.isLoading.account) {
            this.initializeTopBar();
          } else if (!this.isLoading.account) {
            this.buildNavbar();
          }
        }
      });
  }

  async ngOnInit() {
    this.navbarService
      .getMessageFromNavbar()
      .pipe(takeUntil(this.unsub$))
      .subscribe((message) => {
        switch (message.text) {
          case 'rebuild':
            this.authService.getSession().then(() => this.buildNavbar());
        }
      });

    this.alertService
      .getSnackbar()
      .pipe(takeUntil(this.unsub$))
      .subscribe((data) => {
        this.snackbarData = {
          id: data.id,
          message: data.message,
          duration: data.duration,
          mode: data.mode,
          autoDismiss: data.autoDismiss,
        };
      });

    this.groupService.selectedGroup$
      .pipe(
        takeUntil(this.unsub$),
        filter((group) => !!group)
      )
      .subscribe(() => {
        this.storageAccountService.groupFieldsSub.load(true);
        this.visibleGroups = this.groupService.getVisibleGroups();
        this.selectedGroup = this.groupService.getSelectedGroup();
      });

    void (async () => {
      this.stonly = await Stonly.getInstance();
      await this.stonly.init();
      this.user$ = from(this.authService.getSession());
      this.user$
        .pipe(
          takeUntil(this.unsub$),
          distinctUntilChanged(
            (a, b) => a.user.id === b.user.id && a.user.email_address === b.user.email_address
          )
        )
        .subscribe((user) => {
          this.stonly?.patchUserData({
            email: user.user.email_address,
            id: user.user.id,
            accountId: user.user.account_id,
          });
        });
    })();
    this.localStorageService.readLsSignal.set(true);
  }

  private async initializeTopBar() {
    this.isLoading.account = true;
    try {
      setTimeout(() => document.querySelector('body').classList.add('ready'));

      const [account, session, _, preferences, accountCounts] = await Promise.all([
        this.accountService.sub.latest(),
        this.authService.getSession(),
        this.groupService.refreshAndSelectActiveGroup(undefined, false),
        this.preferencesService.getPreferences(),
        this.storageAccountService.getAccountCounts(),
      ]);

      this.account = account;
      this.session = session;
      if (
        !this.groupService.isSessionExpired() &&
        this.groupService.getSelectedGroupId() === undefined
      ) {
        this.alertService.showErrorToast(this.translocoService.translate('app.not-member'));
        this.logout();
      }
      this.visibleGroups = this.groupService.getVisibleGroups();

      this.selectedGroup = this.groupService.getSelectedGroup();
      this.userTimezone = preferences.timezone || this.timezoneService.defaultTimezone;
      this.userHomepage = preferences.homepage || this.groupService.getDefaultHomepage();
      this.userLangCode = preferences.langCode || SemantriaPreferences.DEFAULT_LANGUAGE_CODE;
      this.stonly?.setLanguage(this.userLangCode);
      this.translocoService.setActiveLang(this.userLangCode);

      this.accountCounts = accountCounts;

      const auth_token = this.authService.getToken();
      const telemetryConfig = getTelemetryConfig();
      const loggerConfig: LoggerConfiguration = {
        level: LoggerLevel.DEBUG,
      };
      if (telemetryConfig.enabled) {
        loggerConfig.serverLogLevel = LoggerLevel.ERROR;
        loggerConfig.serverLoggingUrl = telemetryConfig.url;
        loggerConfig.headers = new HttpHeaders({ Authorization: auth_token });
      }
      this.logger.updateConfig(loggerConfig);

      this.buildNavbar();

      // Check if mobile user needs mode selection
      this.checkMobileModeSelector();
    } catch (ex) {
      this.logger.error('Unexpected error during building app top bar', { error: ex });
    } finally {
      this.isLoading.account = false;
    }
  }

  private checkMobileModeSelector(): void {
    setTimeout(() => {
      this.mobileModeService.checkAndShowSelector();
      if (this.mobileModeService.showModeSelector()) {
        this.showMobileModeSelector = true;
      }
    }, 0);
  }

  public onMobileModeSelected(mode: MobileMode): void {
    this.showMobileModeSelector = false;
    if (mode === 'chat') {
      this.openAIAssistantFullscreen();
    }
  }

  private openAIAssistantFullscreen(): void {
    window.dispatchEvent(
      new CustomEvent('open-ai-assistant', { detail: { fullscreen: true, mode: 'ask' } })
    );
  }

  public onTabChange($event) {
    const tab = this.tabMapping.find((t) => t.id === $event.detail.tab.id);
    if (
      !tab.route.includes('configuration-routes') ||
      !this.router.isActive(tab.route, {
        paths: 'subset',
        queryParams: 'subset',
        fragment: 'ignored',
        matrixParams: 'ignored',
      })
    ) {
      this.authService.extendSession();
      this.router.navigateByUrl(`/${tab.route}`);
    }
  }

  private buildNavbar() {
    this.tabMapping = [];
    this.navbarDataLoaded = false;

    this.addTabMapping(
      'projects',
      this.translationResources.projects,
      `groups/${this.groupService.getSelectedGroupId(true)}/projects`,
      'multiline_chart'
    );

    if (!this.isDemoMode) {
      if (!this.authService.isExecutive) {
        this.addTabMapping(
          'configurations',
          this.translationResources.configs,
          `groups/${this.groupService.getSelectedGroupId(true)}/configurations`,
          'square_foot'
        );
        this.addTabMapping(
          'routes',
          this.translationResources.routes,
          `groups/${this.groupService.getSelectedGroupId(true)}/configuration-routes`,
          'account_tree'
        );
        this.addTabMapping(
          'fields',
          this.translationResources.metadata,
          `groups/${this.groupService.getSelectedGroupId(true)}/fields`,
          'text_fields'
        );
        if (this.authService.isAccountAdmin) {
          this.addTabMapping(
            'groups',
            this.translationResources.groups,
            'user-groups',
            'space_dashboard'
          );
        }
        this.addTabMapping('users', this.translationResources.users, 'users', 'people', false);

        if (this.account.features.includes('ACTION_PLANS')) {
          this.addTabMapping(
            'action-plans',
            this.translationResources.plans,
            `groups/${this.groupService.getSelectedGroupId(true)}/action-plans`,
            'square_foot'
          );
        }
      }
    }

    //Map tabMapping to XI Kitchen tab component
    this.tabMenu = [];
    setTimeout(() => {
      this.tabMenu = this.tabMapping.map((obj) => ({
        title: obj.title,
        id: obj.id,
        route: obj.route,
      }));
    });
    this.navbarDataLoaded = true;
  }

  private addTabMapping(id, title, route, icon, disabled = false) {
    if (this.tabMapping.find((x) => x.title === title) === undefined) {
      this.tabMapping.push({
        id,
        route,
        title,
        icon,
        disabled,
      });
    }
  }

  private setTabData(route: string) {
    const tabData = this.appService.syncTabMenu(route);
    this.activeTab = tabData.activeTab;
    this.groupTab = this.activeTab === 'groups';
    this.groupSelectorTooltip = this.getGroupSelectorTooltip();
  }

  onOpenLexalyticsHomepage() {
    window.open('https://lexalytics.com');
  }

  public openChangeTimezone(): void {
    this.showTimezoneModal = true;
  }

  public onTimezoneModalClosed(timezone: string): void {
    this.showTimezoneModal = false;
    if (timezone) {
      this.preferencesService
        .savePreference('timezone', timezone)
        .then(() => {
          this.userTimezone = timezone;
          window.location.reload();
        })
        .catch((error) => {
          this.alertService.showErrorToast(
            this.translocoService.translate('app.timezone-failed'),
            error
          );
        });
    }
  }

  onOpenChangeLanguage(): void {
    this.isChangeLanguageModalOpen = true;
  }

  onCloseChangeLanguage(langCode: string | null): void {
    this.isChangeLanguageModalOpen = false;

    if (langCode) {
      this.preferencesService
        .savePreference('langCode', langCode)
        .then(() => {
          this.userLangCode = langCode;
          this.stonly?.setLanguage(this.userLangCode);
          this.translocoService.setActiveLang(langCode);

          window.location.reload();
        })
        .catch((error) =>
          this.alertService.showErrorToast(
            this.translocoService.translate('app.language-failed'),
            error
          )
        );
    }
  }

  public get isDemoMode(): boolean {
    return this.authService.isDemoMode;
  }

  public get isReadOnly(): boolean {
    return this.authService.isReadOnly;
  }

  public goHome(): void {
    this.router.navigate([this.userHomepage]);
  }

  public goToXI(): void {
    const group = this.groupService.getSelectedGroup();
    const externalId = group?.external_id || '';
    const appConfig = (window as any).appConfig;
    const home = appConfig.inmoment?.home || 'https://cloud.inmoment.com';
    window.location.replace(`${home}?programId=${externalId}`);
  }

  @HostListener('window:open-download-center', ['$event'])
  public openDownloads($event: any): void {
    this.showDownloads();

    const element = $event.target as HTMLElement;
    const parentElement = element.parentElement.parentElement;
    const pointer = parentElement.getElementsByClassName('pointer');
    if (pointer.length) {
      const dismissElement = pointer[0] as HTMLElement;
      dismissElement.click();
    }
  }

  public showDownloads(): void {
    this.showDownloadCenter = true;
  }

  onDownloadCenterClosed() {
    this.showDownloadCenter = false;
  }

  @HostListener('window:reconnect-websocket', ['$event'])
  public reconnectWebsocket($event: CustomEvent<any>): void {
    const { data } = $event.detail;
    const { socket, socketConsumer } = data;
    this.realtimeService.triggerManualRetry({ socket, socketConsumer });
    const element = $event.target as HTMLElement;
    const parentElement = element.parentElement.parentElement;
    const pointer = parentElement.getElementsByClassName('pointer');
    if (pointer.length) {
      const dismissElement = pointer[0] as HTMLElement;
      dismissElement.click();
    }
  }
  public onHomepageChanged(homepage: string) {
    this.userHomepage = homepage;
  }

  public get canShow(): boolean {
    return this.router.url !== '/' && !this.router.url.startsWith('/login');
  }

  private setState(routerOutlet: RouterOutlet): string {
    if (!routerOutlet?.isActivated) {
      return;
    }

    const state = this.appService.getStateFromRoute(routerOutlet.activatedRoute.snapshot);
    this.appService.setState(state);
  }

  public async logout() {
    this.configService.clearEvaluationConfigs();
    await this.authService.logout();
  }

  private openSessionWarning(data: ExpirationEvent) {
    this.expiration = data.expiration;
    this.sessionExpiredDialogRef = this.dialogService.showDialog(
      SessionExpirationWarningComponent,
      {
        expiration: this.expiration,
      },
      { maxWidth: '500px' }
    );

    this.sessionExpiredDialogRef
      .afterClosed()
      .subscribe(() => this.onSessionWarningModalClosed({ extendSession: true }));
  }

  public async onSessionWarningModalClosed(result) {
    this.sessionExpiredDialogRef = undefined;
    if (result) {
      await this.authService.updateSession();
      this.logger.info('session extended');
    }
  }

  private reset(data: LogoutEvent) {
    this.account = undefined;
    this.session = undefined;
    let urlParams: Params = {};

    if (data.clear) {
      if (/[?]/.test(window.location.search)) {
        urlParams = this.serializer.parse(window.location.search).queryParams;
      }
      urlParams.error = data.reason;
      urlParams.session = null;
    }

    const appVolatile = (window as any).appVolatile;
    appVolatile.redirectTo = window.location.pathname + window.location.search;
    if (data.redirectTo) {
      document.location.replace(data.redirectTo);
    } else {
      this.router.navigate(['login'], { queryParams: urlParams }).then(() => {
        if (this.sessionExpiredDialogRef) {
          this.sessionExpiredDialogRef.close();
        }
      });
    }
  }

  private stripMatrixParams(url: string): string {
    const [path, query] = url.split('?');
    const stripped = path
      .split('/')
      .map((seg) => seg.split(';')[0])
      .join('/');
    return query ? `${stripped}?${query}` : stripped;
  }

  private getGroupSelectorTooltip(): string {
    return this.groupTab ? this.translationResources.groupselect : '';
  }

  ngOnDestroy() {
    this.stonly?.destroy();
    this.unsub$.next();
    this.unsub$.complete();
  }
}
